import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';

import {
  validateSchemaForm,
  type ValidateSchemaFormOptions,
} from '@/utils/validation/validateSchemaForm';

import type { ContractValidationScope } from '@/utils/validation/contractValidation';
import type { FormSchema } from '@/utils/validation/formSchemaKit';
import type { ValidationScope } from '@/utils/validation/jobProgressFields';
import type { ValidateWithSchemaResult } from '@/utils/validation/zodToAntdFieldErrors';
import type { FormInstance } from 'antd/es/form';

type ValidateForm = Pick<
  FormInstance,
  'getFieldsValue' | 'setFields' | 'scrollToField'
> & {
  setFieldsValue?: FormInstance['setFieldsValue'];
  getFieldInstance?: FormInstance['getFieldInstance'];
};

type FormSchemaContextValue = {
  formSchema: FormSchema;
  validationScope: ValidationScope;
  contractValidationScope?: ContractValidationScope;
  form?: ValidateForm;
  formName?: string;
};

const FormSchemaContext = createContext<FormSchemaContextValue | null>(null);

export function FormSchemaProvider({
  formSchema,
  validationScope = 'save',
  contractValidationScope,
  form,
  formName,
  children,
}: {
  formSchema: FormSchema;
  validationScope?: ValidationScope;
  contractValidationScope?: ContractValidationScope;
  form?: ValidateForm;
  formName?: string;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({
      formSchema,
      validationScope,
      contractValidationScope,
      form,
      formName,
    }),
    [formSchema, validationScope, contractValidationScope, form, formName],
  );

  return (
    <FormSchemaContext.Provider value={value}>
      {children}
    </FormSchemaContext.Provider>
  );
}

export function useFormSchema(): FormSchema | null {
  return useContext(FormSchemaContext)?.formSchema ?? null;
}

export function useValidationScope(): ValidationScope {
  return useContext(FormSchemaContext)?.validationScope ?? 'save';
}

export function useContractValidationScope():
  ContractValidationScope | undefined {
  return useContext(FormSchemaContext)?.contractValidationScope;
}

export function useSchemaFormValidate(): (
  scope?: ValidationScope,
  options?: Omit<
    ValidateSchemaFormOptions,
    'validationScope' | 'contractValidationScope'
  >,
) => ValidateWithSchemaResult {
  const context = useContext(FormSchemaContext);

  return useCallback(
    (scope, options) => {
      if (context?.formSchema == null || context.form == null) {
        const values = (context?.form?.getFieldsValue(true) ?? {}) as Record<
          string,
          unknown
        >;
        return { ok: true, data: values };
      }

      const values = context.form.getFieldsValue(true) as Record<
        string,
        unknown
      >;
      return validateSchemaForm(context.form, context.formSchema, values, {
        validationScope: scope ?? context.validationScope,
        contractValidationScope: context.contractValidationScope,
        formName: context.formName,
        ...options,
      });
    },
    [context],
  );
}
